import {
  type AdminBffClient,
  type AdminBffMethodNames,
  type AdminBffMethods,
  adminBffMethods,
} from '@my-medication/bff-interfaces-ui/infrastructure/admin/repository.types';
import { useCallback, useMemo, useRef } from 'react';

import {
  resolveDefaultStorage,
  generateCorrelationId,
  generateSessionId,
  inMemoryStore,
} from '@my-medication/shared-components/hooks/useBffApi';
import {
  DEFAULT_SESSION_ID_KEY,
  type Fetcher,
  HttpError,
  IMPERSONATION_TOKEN_STORAGE_STARTING_VALUE,
  TOKEN_STORAGE_KEY,
  type UseBffApiConfig,
  X_CORRELATION_ID_HEADER,
  X_SESSION_ID_HEADER,
} from '@my-medication/shared-components/hooks/useBffApi';
import { notifyAdminUnauthorized } from './unauthorized';

const defaultFetcher: Fetcher = (url, init) => globalThis.fetch(url, init);

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function storeSessionId(sessionId: string): void {
  if (typeof globalThis !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- indexing an arbitrary global
    (globalThis as any)[DEFAULT_SESSION_ID_KEY] = sessionId;
  } else {
    inMemoryStore[DEFAULT_SESSION_ID_KEY] = sessionId;
  }
}

function getSessionId(): string {
  const existing =
    typeof globalThis !== 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- indexing an arbitrary global
        (globalThis as any)[DEFAULT_SESSION_ID_KEY]
      : inMemoryStore[DEFAULT_SESSION_ID_KEY];
  if (!existing) {
    const newId = generateSessionId();
    storeSessionId(newId);
    return newId;
  }
  return existing;
}

function useAdminBffApi({
  baseUrl = '/api/v1/admin',
  storage,
  fetcher = defaultFetcher,
}: UseBffApiConfig = {}): AdminBffClient {
  const effectiveStorage: Storage = storage ?? resolveDefaultStorage();

  const doFetch = useMemo(() => {
    return fetcher;
  }, [fetcher]);

  const buildUrl = useCallback(
    (endpoint: string, queryParams?: Record<string, unknown>) => {
      let queryString = '';
      if (queryParams) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(queryParams)) {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        }
        queryString = params.toString();
      }
      return `${baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;
    },
    [baseUrl],
  );

  const getToken = useCallback(() => effectiveStorage.getItem(TOKEN_STORAGE_KEY) || '', [effectiveStorage]);

  const isImpersonation = useCallback(
    () => getToken().startsWith(IMPERSONATION_TOKEN_STORAGE_STARTING_VALUE),
    [getToken],
  );

  const execute = useCallback(
    async function executeRequest<T extends AdminBffMethods, M extends AdminBffMethodNames>({
      method,
      queryParams,
      body,
      locale = 'de',
      refreshTokenCallback,
      isRetry = false,
    }: {
      method: M;
      queryParams?: T[M]['queryType'];
      body?: T[M]['bodyType'];
      locale?: string;
      refreshTokenCallback?: () => Promise<{ token: string; refreshToken: string } | null | undefined>;
      isRetry?: boolean;
    }): Promise<T[M]['returnType']> {
      const { callMethod, endpoint } = adminBffMethods[method];

      const correlationId = generateCorrelationId();
      const sessionId = getSessionId();

      const url = buildUrl(endpoint, queryParams as Record<string, unknown> | undefined);

      const init: RequestInit = {
        method: callMethod,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Accept-Language': locale,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          ...(isImpersonation() && { Authorization: getToken() }),
          [X_CORRELATION_ID_HEADER]: correlationId,
          [X_SESSION_ID_HEADER]: sessionId,
        },
        ...(body && { body: JSON.stringify(body) }),
      };

      const response = await doFetch(url, init);
      const data = await parseResponseBody(response);

      if (!response.ok) {
        const error = new HttpError(response.status, data);
        if (response.status === 401 && refreshTokenCallback && !isRetry) {
          const refreshed = await refreshTokenCallback();
          if (refreshed) {
            return executeRequest<T, M>({
              method,
              queryParams,
              body,
              locale,
              refreshTokenCallback,
              isRetry: true,
            });
          }
        }

        if (response.status === 401) {
          notifyAdminUnauthorized();
        }

        throw error;
      }

      return data as T[M]['returnType'];
    },
    [doFetch, baseUrl, buildUrl, getToken, isImpersonation],
  );

  const executeRef = useRef(execute);
  executeRef.current = execute;

  // The overloaded generic signature cannot survive useCallback's type system, so it
  // is erased here and restored by the cast below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stableExecute = useCallback((arg: any) => executeRef.current(arg), []) as unknown as typeof execute;

  return {
    execute: stableExecute,
  };
}

export { useAdminBffApi };
export default useAdminBffApi;
